import { useEffect, useState } from "react";
import { listPageSections } from "@/lib/cms.functions";

type Section = {
  id: string;
  page_slug: string;
  section_key: string;
  type: string;
  sort_order: number;
  status: string;
  content: { heading?: string; body?: string; cta_label?: string; cta_href?: string } & Record<string, unknown>;
};

interface Props {
  pageSlug: string;
  /** Section keys already covered by static blocks on the page — published CMS
   *  sections with these keys are skipped to prevent duplicate content. */
  reservedKeys?: string[];
  className?: string;
}

const DEFAULT_RESERVED: Record<string, string[]> = {
  // Homepage already statically renders these. Admin can use any other section_key.
  home: [
    "hero", "proof", "reassurance", "services", "service-picker", "service_picker",
    "why", "why-choose-us", "trust", "compliance", "reviews", "testimonials",
    "what-to-expect", "process", "vehicles", "vehicle-types", "gallery",
    "featured-photos", "workshop", "areas", "find-us", "directions",
    "arrival-faq", "faq", "faqs", "blog", "related-blog", "instagram",
    "compliance-block", "quick-contact", "contact", "cta", "final-cta",
  ],
};

/** Renders all PUBLISHED cms_sections for a given page slug, in sort order.
 *  - Skips empty sections (no heading + body + CTA)
 *  - Skips reserved section_keys that already have a static block on the page,
 *    preventing duplicate hero/services/reviews/FAQ content.
 *  Editable from admin → "CMS: Page Sections". */
export function CmsSections({ pageSlug, reservedKeys, className = "" }: Props) {
  const [items, setItems] = useState<Section[]>([]);

  useEffect(() => {
    let cancelled = false;
    listPageSections({ data: { pageSlug } })
      .then((r) => { if (!cancelled) setItems(r.items as Section[]); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pageSlug]);

  const reserved = new Set((reservedKeys ?? DEFAULT_RESERVED[pageSlug] ?? []).map((k) => k.toLowerCase()));

  const visible = items
    .filter((s) => s.status === "published")
    .filter((s) => !reserved.has((s.section_key || "").toLowerCase()))
    .filter((s) => {
      const c = s.content || {};
      const hasContent = (c.heading && String(c.heading).trim())
        || (c.body && String(c.body).trim())
        || (c.cta_label && c.cta_href);
      return Boolean(hasContent);
    })
    .sort((a, b) => a.sort_order - b.sort_order);

  if (!visible.length) return null;

  return (
    <div className={className}>
      {visible.map((s) => {
        const c = s.content || {};
        return (
          <section key={s.id} className="py-12 md:py-16">
            <div className="mx-auto max-w-4xl px-4 md:px-6 text-center">
              {c.heading && (
                <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">{c.heading}</h2>
              )}
              {c.body && (
                <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto whitespace-pre-line">
                  {c.body}
                </p>
              )}
              {c.cta_label && c.cta_href && /^(https?:\/\/|\/|mailto:|tel:)/i.test(String(c.cta_href)) && (
                <a
                  href={c.cta_href}
                  className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  {c.cta_label}
                </a>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
